import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Check, Globe, Loader2, RefreshCw, Search, Sparkles, XCircle } from "lucide-react";
import useApiMethods from "../../../lib/useApiMethods";
import ToastMsg from "../../common/ToastMsg";
import {
    RESEARCH_CANCEL_URL, RESEARCH_IDENTITY_URL, RESEARCH_JOB_URL, RESEARCH_LEAD_URL,
    RESEARCH_REVIEW_URL, RESEARCH_START_URL,
} from "../../../constants/api";
import { cn } from "../../../utils/tailwindUtils";
import CompanyResearchReport from "./CompanyResearchReport";
import { RESEARCH_CHANGED_EVENT } from "./LeadStatusChips";

/**
 * Company Research tab on the lead page (trustedge research/).
 *
 * Research runs in the local AI worker (Claude Code on the office PC), so a
 * job can wait in the queue until that worker picks it up. While a job is
 * queued or running this polls the job every few seconds; the rest of the
 * page stays usable.
 */
// Fast enough to watch the activity feed move; one small request per tick.
const POLL_MS = 2500;
const ACTIVE = ["queued", "running"];

function StatusPill({ job }) {
    const tone = {
        completed: "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300",
        partial: "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300",
        needs_review: "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300",
        failed: "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300",
    }[job.status] || "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300";
    return <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", tone)}>{job.status_label}</span>;
}

function WebsiteForm({ onSubmit, busy, label = "Research this website" }) {
    const [site, setSite] = useState("");
    return (
        <form className="flex flex-wrap items-center gap-2" onSubmit={e => { e.preventDefault(); if (site.trim()) onSubmit(site.trim()); }}>
            <label className="sr-only" htmlFor="research-website">Website</label>
            <input id="research-website" value={site} onChange={e => setSite(e.target.value)} placeholder="example.com"
                className="h-8 w-56 max-w-full rounded-md border border-gray-300 px-2 text-xs dark:border-gray-600 dark:bg-gray-800" />
            <button type="submit" disabled={!site.trim() || busy}
                className="h-8 rounded-md border border-gray-300 px-3 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:text-gray-200">
                {label}
            </button>
        </form>
    );
}

function Progress({ job, canEdit, onCancel, busy }) {
    const current = job.stages.findIndex(s => s.key === job.stage);
    return (
        <div className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700" aria-live="polite">
            <div className="flex items-center justify-between gap-2">
                <p className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden="true" />
                    {job.status === "queued" ? "Waiting for the AI worker" : job.stage_label || "Starting"}
                </p>
                {canEdit && !job.cancel_requested && (
                    <button type="button" onClick={onCancel} disabled={busy}
                        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600">
                        <XCircle className="h-3.5 w-3.5" aria-hidden="true" /> Cancel
                    </button>
                )}
            </div>
            {job.status === "queued" && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    Research runs in the Gro Wing AI Worker on the office PC. It starts within a minute while the worker is watching for jobs; otherwise at its next scheduled run.
                </p>
            )}
            {job.cancel_requested && <p className="text-xs text-gray-500">Stopping after the current step…</p>}
            <ol className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-6">
                {job.stages.map((s, i) => (
                    <li key={s.key} className={cn("rounded-md px-2 py-1.5 text-[11px]",
                        i < current ? "bg-brand-50 text-primary dark:bg-brand-900/20"
                            : i === current ? "bg-primary text-white" : "bg-gray-50 text-gray-400 dark:bg-gray-700/40")}>
                        {i < current && <Check className="mr-0.5 inline h-3 w-3" aria-hidden="true" />}{s.label}
                    </li>
                ))}
            </ol>
            {job.status === "running" && (
                <>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-600 dark:text-gray-300">
                        <span><b className="tabular-nums">{job.sources_count}</b> page{job.sources_count === 1 ? "" : "s"} read</span>
                        <span><b className="tabular-nums">{job.facts_count}</b> fact{job.facts_count === 1 ? "" : "s"} found</span>
                        {job.elapsed != null && <span><b className="tabular-nums">{elapsedLabel(job.elapsed)}</b> elapsed</span>}
                    </div>
                    <ActivityFeed items={job.activity} />
                </>
            )}
        </div>
    );
}

const elapsedLabel = (s) => (s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`);
const FEED_ICON = { ai: Sparkles, done: Check, warn: AlertTriangle };

/** What the agent is doing right now, newest at the bottom (report.activity). */
function ActivityFeed({ items = [] }) {
    const end = useRef(null);
    useEffect(() => { end.current?.scrollIntoView?.({ block: "nearest" }); }, [items.length]);
    if (!items.length) return null;
    return (
        <ol aria-label="Research activity" className="max-h-56 space-y-1 overflow-y-auto rounded-md bg-gray-50 p-2 dark:bg-gray-900/40">
            {items.map((a, i) => {
                const last = i === items.length - 1;
                const Icon = last ? Loader2 : FEED_ICON[a.kind] || Check;
                return (
                    <li key={i} className={cn("flex items-start gap-1.5 text-[11px]",
                        last ? "font-medium text-gray-900 dark:text-white" : "text-gray-600 dark:text-gray-300",
                        a.kind === "warn" && "text-amber-700 dark:text-amber-300")}>
                        <Icon className={cn("mt-0.5 h-3 w-3 shrink-0", last ? "animate-spin text-primary"
                            : a.kind === "ai" ? "text-primary" : a.kind === "warn" ? "text-amber-500" : "text-green-600")}
                            aria-hidden="true" />
                        <span className="min-w-0 flex-1 break-words">{a.text}</span>
                        <span className="shrink-0 tabular-nums text-gray-400">{elapsedLabel(a.t)}</span>
                    </li>
                );
            })}
            <li ref={end} aria-hidden="true" />
        </ol>
    );
}

function NeedsReview({ job, canEdit, onPick, busy }) {
    return (
        <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/50 p-4 dark:border-amber-800 dark:bg-amber-900/10">
            <p className="text-sm font-medium text-gray-900 dark:text-white">Which organisation is this?</p>
            {job.notes?.map((n, i) => <p key={i} className="text-xs text-gray-600 dark:text-gray-300">{n}</p>)}
            {job.candidates?.length > 0 && (
                <ul className="space-y-2">
                    {job.candidates.map(c => (
                        <li key={c.url} className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-800">
                            <div className="min-w-0">
                                <a href={c.url} target="_blank" rel="noopener noreferrer" className="break-all text-xs font-medium text-primary hover:underline">{c.title || c.url}</a>
                                <p className="break-all text-[10px] text-gray-500">{c.url} · {c.origin} · match {c.score ?? 0}/100</p>
                                {c.reasons?.length > 0 && <p className="text-[11px] text-gray-500 dark:text-gray-400">{c.reasons.join("; ")}</p>}
                            </div>
                            {canEdit && c.score > 0 && (
                                <button type="button" disabled={busy} onClick={() => onPick(c.url)}
                                    className="h-7 rounded-md bg-primary px-2.5 text-[11px] font-medium text-white hover:opacity-90 disabled:opacity-40">
                                    This is it
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
            {canEdit && (
                <div className="space-y-1">
                    <p className="text-xs text-gray-600 dark:text-gray-300">Or enter their website:</p>
                    <WebsiteForm onSubmit={onPick} busy={busy} />
                </div>
            )}
        </div>
    );
}

export default function CompanyResearchPanel({ customerId }) {
    const { fetch, create } = useApiMethods();
    const [state, setState] = useState(null);       // {enabled, can_start, job, history}
    const [busy, setBusy] = useState(false);
    const [view, setView] = useState("Overview");
    const [tick, setTick] = useState(0);
    const timer = useRef(null);

    const load = useCallback(() => {
        if (!customerId) return Promise.resolve();
        return fetch(RESEARCH_LEAD_URL(customerId)).then(res => {
            if (res?.status === "success") setState(res.data);
            else setState(s => s || { error: res?.message || "Could not load research" });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    useEffect(() => { setState(null); load(); }, [load]);

    const job = state?.job;
    const active = job && ACTIVE.includes(job.status);

    // Keeps the badge next to the lead's name in step (LeadStatusChips).
    useEffect(() => {
        if (job) window.dispatchEvent(new CustomEvent(RESEARCH_CHANGED_EVENT, { detail: { customerId } }));
    }, [customerId, job?.id, job?.status, job?.stage, job?.pending_reviews]); // eslint-disable-line react-hooks/exhaustive-deps

    // Poll the job while it is queued or running; reload history when it ends.
    useEffect(() => {
        if (!active) return undefined;
        timer.current = setTimeout(() => {
            fetch(RESEARCH_JOB_URL(job.id)).then(res => {
                if (res?.status === "success" && !ACTIVE.includes(res.data.status)) return load();
                if (res?.status === "success") setState(s => ({ ...s, job: res.data }));
                setTick(t => t + 1);        // a failed poll tries again
            });
        }, POLL_MS);
        return () => clearTimeout(timer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, job?.id, tick]);

    const run = async (call, ok) => {
        setBusy(true);
        try {
            const res = await call();
            if (res?.status === "success") { ok?.(res); return true; }
            ToastMsg("error", res?.message || "Something went wrong");
            return false;
        } finally { setBusy(false); }
    };

    const start = (opts = {}) => run(() => create(RESEARCH_START_URL(customerId), opts), res => {
        setState(s => ({ ...s, job: res.data }));
        setView("Overview");
        if (!res.created) ToastMsg("info", ACTIVE.includes(res.data.status)
            ? "Research is already running for this lead"
            : "Showing the recent report. Use Refresh to research again.");
    });
    const cancel = () => run(() => create(RESEARCH_CANCEL_URL(job.id), {}), res => setState(s => ({ ...s, job: res.data })));
    const pick = (website) => run(() => create(RESEARCH_IDENTITY_URL(job.id), { website }),
        res => setState(s => ({ ...s, job: res.data })));
    const review = (decisions) => run(() => create(RESEARCH_REVIEW_URL(job.id), { decisions }), res => {
        const n = res.result?.applied?.length || 0;
        ToastMsg("success", n ? `${n} CRM field${n === 1 ? "" : "s"} updated` : "Saved");
        setState(s => ({ ...s, job: res.data }));
        load();
    });

    if (!state) {
        return (
            <div className="space-y-2" aria-busy="true">
                {[0, 1, 2].map(i => <div key={i} className="h-10 animate-pulse rounded-md bg-gray-100 dark:bg-gray-700/50" />)}
            </div>
        );
    }
    if (state.error) return <p className="text-xs text-red-600">{state.error}</p>;

    const canEdit = state.can_start;
    const done = job && ["completed", "partial"].includes(job.status);

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                        <Search className="h-4 w-4 text-primary" aria-hidden="true" />
                        {job?.resolved_name || "Company research"}
                        {job && <StatusPill job={job} />}
                    </h3>
                    {job?.website && (
                        <a href={job.website} target="_blank" rel="noopener noreferrer"
                            className="mt-0.5 inline-flex items-center gap-1 text-xs text-primary hover:underline">
                            <Globe className="h-3 w-3" aria-hidden="true" />{job.website}
                        </a>
                    )}
                    {job?.finished_at && <p className="text-[11px] text-gray-500 dark:text-gray-400">Researched {job.finished_at}{job.requested_by ? ` · ${job.requested_by}` : ""}</p>}
                </div>
                {canEdit && state.enabled && job && !active && (
                    <button type="button" disabled={busy} onClick={() => start({ refresh: true })}
                        className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-white hover:opacity-90 disabled:opacity-40">
                        <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" /> Refresh research
                    </button>
                )}
            </div>

            {!state.enabled && !job && (
                <p className="text-xs text-gray-500">Company research is switched off for this organisation (Settings → Company Research).</p>
            )}

            {!job && state.enabled && (
                <div className="space-y-3 rounded-lg border border-dashed border-gray-300 p-5 text-center dark:border-gray-600">
                    <p className="text-sm text-gray-700 dark:text-gray-200">Find out who this organisation is, how to reach them and what to talk about.</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        The agent finds their website, reads its public pages and reports only what those pages say, with the source next to every fact. Nothing in the CRM changes until you approve it.
                    </p>
                    {canEdit ? (
                        <div className="flex flex-col items-center gap-2">
                            <button type="button" disabled={busy} onClick={() => start()}
                                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-4 text-xs font-medium text-white hover:opacity-90 disabled:opacity-40">
                                <Search className="h-3.5 w-3.5" aria-hidden="true" /> Research company
                            </button>
                            <p className="text-[11px] text-gray-500">Know the website already?</p>
                            <WebsiteForm busy={busy} onSubmit={website => start({ website })} />
                        </div>
                    ) : <p className="text-xs text-gray-400">You need edit access to customers to start research.</p>}
                </div>
            )}

            {active && <Progress job={job} canEdit={canEdit} onCancel={cancel} busy={busy} />}
            {job?.status === "needs_review" && <NeedsReview job={job} canEdit={canEdit} onPick={pick} busy={busy} />}
            {job?.status === "failed" && (
                <div className="rounded-lg border border-red-200 bg-red-50/60 p-3 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300" role="alert">
                    Research failed: {job.error || "unknown error"}. {canEdit && "Press Refresh research to try again."}
                </div>
            )}
            {job?.status === "partial" && (
                <p className="rounded-md bg-amber-50 p-2 text-[11px] text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                    Partial result: part of the research could not run (see Overview → What the agent did). What is shown was still checked against its source.
                </p>
            )}

            {done && (
                <CompanyResearchReport job={job} history={state.history} view={view} onView={setView}
                    canEdit={canEdit} onReview={review} busy={busy} />
            )}
        </div>
    );
}
